// @flow
import * as React from 'react';
import { Row, Col, Button, ButtonToolbar } from 'react-bootstrap';
import fuzzyFilterFactory from 'react-fuzzy-filter';
import { CSSTransitionGroup } from 'react-transition-group';
import { formatDate } from '../../helpers';
import PaginatedList from '../../components/PaginatedList';
import ListFilterBox from '../../components/ListFilterBox';
import SelectDropdown from '../../components/SelectDropdown';

type Props = {
  params: {
    customerId: any
  },
  getCurrentOrganisationShapeSets: Function,
  removeShapeSetFromCurrentOrganisation: Function,
  addShapeSetToCurrentOrganisation: Function,
  currentOrganisation: {
    shapeSets: any
  },
  route: any,
  router: any,
  authentication: any,
  currentCustomer: any
};

type State = {
  newShapeSetId: ?any,
  showNewShapeSet: boolean,
  isSaving?: boolean,
  errorMsg?: any,
  successMsg?: any,
  filterValue?: any
};

const ControlHeader = props => (
  <Row className="tools">
    <Col sm={4} className="table-header">
      <ListFilterBox
        onChange={props.filterBoxChange}
        fuzzyFilterFactory={props.fuzzyFilterFactory}
      />
    </Col>
    <Col sm={8} className="table-header">
      {!props.hideButtons && (
        <ButtonToolbar>
          <Button
            bsStyle="primary"
            type="submit"
            className="relative-padding pull-right"
            onClick={props.addButtonClick}
          >
            Upload New
          </Button>
          <Button
            bsStyle="default"
            type="submit"
            className="relative-padding pull-right"
            onClick={props.assignButtonClick}
          >
            Assign
          </Button>
        </ButtonToolbar>
      )}
    </Col>
  </Row>
);

const AddNewRow = props => (
  <Row>
    <Col sm={4} className="table-entry">
      <SelectDropdown
        onChange={props.selectOnChange}
        value={props.selectValue}
        options={props.items}
        aria-label="Catchment Set Select Box"
        focus={true}
      />
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        className="relative-padding"
        bsStyle="default"
        onClick={props.addItem}
      >
        Add
      </Button>
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        bsStyle="link"
        className="relative-padding"
        onClick={props.cancel}
      >
        Cancel
      </Button>
    </Col>
  </Row>
);

const LineItem = props => {
  const shapeSet = props.item;
  return (
    <Row key={shapeSet.id} className={shapeSet.isArchived && 'archived'}>
      <Col sm={3} className="table-entry">
        {shapeSet.name}
      </Col>
      <Col sm={3} className="table-entry" />
      <Col sm={2} className="table-entry">
        {formatDate(shapeSet.dateCreated, true)}
      </Col>
      <Col sm={2} className="table-entry">
        {formatDate(shapeSet.dateUpdated, true)}
      </Col>
      <Col sm={1} className="table-entry">
        {!shapeSet.inheritedFromIndustry &&
          !shapeSet.isOwner &&
          props.isGlobalUser && (
            <Button
              className="action-button"
              bsStyle="link"
              onClick={() => props.removeItem(shapeSet)}
              aria-label={`Remove ${shapeSet.name}`}
            >
              Remove
            </Button>
          )}
      </Col>
    </Row>
  );
};

class CustomerShapeSets extends React.Component<Props, State> {
  fuzzyFilterFactory: Function;
  timeoutId: any;
  screenReaderContent: any;
  static displayName = 'CustomerInsightSets';

  constructor(props: Props) {
    super(props);
    this.state = {
      newShapeSetId: undefined,
      showNewShapeSet: false,
      filterValue: ''
    };
    this.fuzzyFilterFactory = fuzzyFilterFactory();
    this.timeoutId = undefined;
  }

  componentDidMount() {
    this.getShapeSets();
    this.screenReaderContent.focus();
  }

  componentWillUnmount() {
    // clear timeout to prevent setState on unmounted component
    clearTimeout(this.timeoutId);
  }

  getShapeSets = () => {
    const id = parseInt(this.props.params.customerId, 10);
    this.props.getCurrentOrganisationShapeSets(id, true);
    this.props.getCurrentOrganisationShapeSets(id, false);
  };

  removeShapeset = (shapeset: any) => {
    this.setState({
      isSaving: true,
      errorMsg: undefined,
      successMsg: undefined
    });

    const organisationId = parseInt(this.props.params.customerId, 10);
    this.props
      .removeShapeSetFromCurrentOrganisation(shapeset, organisationId)
      .then(response => this.success(response), error => this.failure(error));
  };

  addShapeSet = () => {
    if (this.state.newShapeSetId) {
      this.setState({
        isSaving: true,
        errorMsg: undefined,
        successMsg: undefined
      });

      const organisationId = parseInt(this.props.params.customerId, 10);
      const newShapeSetId = this.state.newShapeSetId;
      this.props
        .addShapeSetToCurrentOrganisation(newShapeSetId, organisationId)
        .then(response => this.success(response), error => this.failure(error));
    }
    this.setState({ showNewShapeSet: false, newShapeSetId: undefined });
  };

  success = (response: any) => {
    this.setState({
      isSaving: false,
      errorMsg: undefined,
      successMsg: 'Success!'
    });
    this.timeoutId = setTimeout(
      () => this.setState({ successMsg: undefined }),
      3000
    );
  };

  failure = (error: Object) => {
    this.setState({ isSaving: false, errorMsg: error.data.message });
  };

  updateFilterValue = (filterValue: any) => {
    this.setState({ filterValue: filterValue || '' });
  };

  addButtonClick = () => {
    const { route: { context }, params: { customerId = 0 } } = this.props;
    const path = context === 'global' ? '/customers' : '/customer';
    localStorage.setItem('backUrl', `${path}/${customerId}/catchment-set-list`);
    const shapeSetUrl = `/customer/${customerId}/catchment-sets/new`;
    this.props.router.push(shapeSetUrl);
  };

  assignButtonClick = () => {
    this.setState({ showNewShapeSet: !this.state.showNewShapeSet });
  };

  cancel = () => {
    this.setState({ showNewShapeSet: false, newShapeSetId: undefined });
  };

  selectOnChange = (newShapeSetId: any) => {
    this.setState({ newShapeSetId });
  };

  render() {
    const { allocated, unallocated } = this.props.currentOrganisation.shapeSets;
    const isGlobalUser = this.props.authentication.user.isInternalAdminUser;

    const LineItemWrapper = props => (
      <LineItem
        {...props}
        removeItem={this.removeShapeset}
        isGlobalUser={isGlobalUser}
      />
    );

    return (
      <div>
        <Row />
        <h3
          className="screen-reader-only"
          tabIndex="-1"
          ref={i => (this.screenReaderContent = i)}
        >
          Showing catchment sets for customer {this.props.currentCustomer.name}
        </h3>

        <ControlHeader
          filterBoxChange={this.updateFilterValue}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          addButtonClick={this.addButtonClick}
          assignButtonClick={this.assignButtonClick}
          hideButtons={!isGlobalUser}
        />

        <Row>
          <Col sm={3} className="table-header">
            Catchment Set name
          </Col>
          <Col sm={3} className="table-header">
            Managed by
          </Col>
          <Col sm={2} className="table-header">
            Date created
          </Col>
          <Col sm={2} className="table-header">
            Date updated
          </Col>
          <Col sm={1} className="table-header">
            Action
          </Col>
        </Row>
        <hr />

        {this.state.showNewShapeSet && (
          <AddNewRow
            items={unallocated}
            addItem={this.addShapeSet}
            selectValue={this.state.newShapeSetId}
            selectOnChange={this.selectOnChange}
            cancel={this.cancel}
          />
        )}

        <PaginatedList
          filterValue={this.state.filterValue}
          component={LineItemWrapper}
          items={allocated}
          showArchived={true}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          pluralName="catchment sets"
        />
        <Row>
          <Col sm={12}>
            <CSSTransitionGroup
              transitionName="fade"
              transitionEnterTimeout={300}
              transitionLeaveTimeout={300}
            >
              {this.state.successMsg && (
                <p className="text-success">{this.state.successMsg}</p>
              )}
            </CSSTransitionGroup>
          </Col>
        </Row>
        {this.state.errorMsg && (
          <div className="alert alert-danger">{this.state.errorMsg}</div>
        )}
      </div>
    );
  }
}

export default CustomerShapeSets;



// WEBPACK FOOTER //
// ./src/containers/customer/CustomerShapeSets.js